<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, reactive, watch } from "vue";
import ChevronLeft from "#/assets/iconsDynamic/ChevronLeft.vue";
import ChevronRight from "#/assets/iconsDynamic/ChevronRight.vue";
import { vMuted } from "#/directives/vMuted";
import { useCardStore } from "#/store/card";
import type { IGroupModifiers, ISelectModifiers } from "#/types/iCardProduct";
import { buildExclusiveGroupSelection, getSwiperModifierGroups, pickDefaultSwiperModifier } from "#/utils/customRenderModifiers";
import { getLocaleName } from "#/utils/i18next";

const props = defineProps<{ group?: IGroupModifiers }>();

const { i18next } = useTranslation();
const cardStore = useCardStore();
const { stateCard } = storeToRefs(cardStore);

const animByGroup = reactive<Record<string, "next" | "prev">>({});

const groups = computed<IGroupModifiers[]>(() => {
	if (props.group) return [props.group];
	return getSwiperModifierGroups(stateCard.value?.groupModifiers);
});

const modifiersOf = (group: IGroupModifiers): ISelectModifiers[] => group.group_modifiers ?? [];

const groupIndexOf = (group: IGroupModifiers): number => stateCard.value.groupModifiers.findIndex((item) => item.id === group.id);

const selectedModifierOf = (group: IGroupModifiers): ISelectModifiers | null => {
	const modifiers = modifiersOf(group);
	if (!modifiers.length) return null;

	const index = groupIndexOf(group);
	const selectedId = stateCard.value.groupModifiersSelected[index]?.modifiers?.find((modifier) => (modifier.amount ?? 0) > 0)?.id;
	return modifiers.find((modifier) => modifier.id === selectedId) ?? modifiers[0] ?? null;
};

const selectModifier = (group: IGroupModifiers, modifier: ISelectModifiers) => {
	const index = groupIndexOf(group);
	if (index === -1) return;

	const next = [...(stateCard.value.groupModifiersSelected ?? [])];
	next[index] = buildExclusiveGroupSelection(group, modifier, stateCard.value.selectedSize);
	stateCard.value.groupModifiersSelected = next;
};

const ensureDefaults = () => {
	for (const group of groups.value) {
		const index = groupIndexOf(group);
		if (index === -1) continue;

		const selected = stateCard.value.groupModifiersSelected[index];
		const hasSelection = (selected?.modifiers ?? []).some((modifier) => (modifier.amount ?? 0) > 0);
		if (hasSelection) continue;

		const fallback = pickDefaultSwiperModifier(group);
		if (fallback) selectModifier(group, fallback);
	}
};

const stepGroup = (group: IGroupModifiers, direction: 1 | -1) => {
	const modifiers = modifiersOf(group);
	if (modifiers.length <= 1) return;

	const current = selectedModifierOf(group);
	const currentIndex = Math.max(
		0,
		modifiers.findIndex((modifier) => modifier.id === current?.id),
	);
	const nextIndex = (currentIndex + direction + modifiers.length) % modifiers.length;
	animByGroup[group.id] = direction > 0 ? "next" : "prev";
	selectModifier(group, modifiers[nextIndex]);
};

const pills = computed(() =>
	groups.value.map((group) => {
		const current = selectedModifierOf(group);
		return {
			group,
			current,
			canStep: modifiersOf(group).length > 1,
			name: getLocaleName(current, i18next.language) || "",
			groupName: getLocaleName(group, i18next.language) || "",
			image: current?.image_link || "",
			video: current?.video_link || "",
			animName: animByGroup[group.id] === "prev" ? "csm_prev" : "csm_next",
		};
	}),
);

watch(() => [stateCard.value?.openCard?.id, groups.value.map((group) => group.id).join("|")], ensureDefaults, { immediate: true });
</script>

<template>
	<div v-if="pills.length" class="csm_root">
		<div v-for="pill in pills" :key="pill.group.id" class="csm_pill">
			<button type="button" class="csm_arrow" :disabled="!pill.canStep" @click="stepGroup(pill.group, -1)">
				<ChevronLeft />
			</button>

			<div class="csm_viewport">
				<Transition :name="pill.animName" mode="out-in">
					<div :key="pill.current?.id" class="csm_current">
						<div v-if="pill.image || pill.video" class="csm_media">
							<video v-if="pill.video" :key="pill.video" class="csm_video" autoplay muted v-muted loop playsinline>
								<source :src="pill.video" type="video/webm">
							</video>
							<img v-else class="csm_image" :src="pill.image" :alt="pill.name">
						</div>
						<span class="csm_label">{{ pill.name }}</span>
					</div>
				</Transition>
			</div>

			<button type="button" class="csm_arrow" :disabled="!pill.canStep" @click="stepGroup(pill.group, 1)">
				<ChevronRight />
			</button>
		</div>
	</div>
</template>

<style scoped lang="scss">
  @use './customSwiperModifier.scss';
</style>
